import { useQuery } from '@tanstack/react-query';
import api from '@/libs/axiosInstance';
import type { PayoutBatchPaymentDetails } from '@/types/payouts';

// --- Query Keys ---
const payoutKeys = {
  all: ['payouts'] as const,
  details: (id: string) => [...payoutKeys.all, 'details', id] as const,
};

// --- Fetch Function ---
const fetchPayoutBatchPaymentDetails = async (
  batchId: string
): Promise<PayoutBatchPaymentDetails> => {
  if (!batchId) {
    throw new Error('Batch ID is required to fetch payment details.');
  }
  try {
    const response = await api.get<PayoutBatchPaymentDetails>(
      `/dashboard/payouts/${batchId}/payment-details`
    );
    return response.data;
  } catch (error) {
    // Handle API errors (e.g., log, transform, re-throw)
    console.error('Failed to fetch payout batch payment details:', error);
    // You might want to throw a more specific error based on the response
    throw error;
  }
};

// --- Query Hook ---
interface UseGetPayoutBatchPaymentDetailsOptions {
  enabled?: boolean;
}

export const useGetPayoutBatchPaymentDetails = (
  batchId: string | undefined,
  options?: UseGetPayoutBatchPaymentDetailsOptions
) => {
  return useQuery<PayoutBatchPaymentDetails, Error>({
    queryKey: payoutKeys.details(batchId ?? ''),
    queryFn: () => fetchPayoutBatchPaymentDetails(batchId!),
    enabled: !!batchId && (options?.enabled ?? true), // Only enable if batchId is provided and enabled option is true
    // Optional: Add other react-query options like staleTime, cacheTime, retry, etc.
    // staleTime: 5 * 60 * 1000, // 5 minutes
    refetchOnWindowFocus: false,
  });
};
